import { ReactElement, useMemo } from 'react';
import { useUser } from '../providers/users';
import { Metric } from '../components/Metric';
import { Graph } from '../components/Graph';
import { LineGraph } from '../components/LineGraph';
import { useQuery } from '@apollo/client/react';
import {
  GQL_QUERY_COUNT_USAGE_SUMMARY,
  GQL_QUERY_COUNT_QUERIES,
} from '../utility/queries';
import { subMonths, format } from 'date-fns';
import {
  ExclamationCircleIcon,
  ChatBubbleLeftRightIcon,
  DocumentTextIcon,
  UsersIcon,
} from '@heroicons/react/24/outline';
import { Link } from '@tanstack/react-router';
import { DateGranularity, UserCompanyLimitation } from '@sc/graphql';

const getGreeting = () => {
  const now = new Date();
  if (now.getHours() < 10) return 'morning';
  if (now.getHours() < 15) return 'afternoon';
  return 'evening';
};

export const Dashboard = (): ReactElement => {
  const { user } = useUser();
  const greeting = getGreeting();

  // 12 monthly points for the "over time" trend charts.
  const months = useMemo(
    () =>
      Array.from(new Array(12))
        .map((_, i) => subMonths(new Date(), i))
        .reverse(),
    []
  );

  const { data: usageSummaryData, loading: loadingUsageSummary } = useQuery(
    GQL_QUERY_COUNT_USAGE_SUMMARY,
    {
      context: {
        isBatched: true,
      },
    }
  );

  const { data: yearlyQueryData, loading: loadingYearlyQueryData } = useQuery(
    GQL_QUERY_COUNT_QUERIES,
    {
      variables: {
        dates: months.map((d) => d.toISOString()),
        unique: false,
        granularity: DateGranularity.month,
      },
      context: {
        isBatched: true,
      },
    }
  );

  const { data: yearlyUniqueUserData, loading: loadingYearlyUniqueUserData } =
    useQuery(GQL_QUERY_COUNT_QUERIES, {
      variables: {
        dates: months.map((d) => d.toISOString()),
        unique: true,
        granularity: DateGranularity.month,
      },
      context: {
        isBatched: true,
      },
    });

  const usageSummary = usageSummaryData?.countUsageSummary;

  return (
    <>
      {user.company.limitations.includes(UserCompanyLimitation.cannotCreateQueryMessage) && (
        <Link to='/billing' className="cursor-pointer border border-red-400 bg-red-1000 mt-5 rounded-xs p-1 flex mx-5">
          <div className="p-3 flex">
            <ExclamationCircleIcon className="size-5 text-red-400" />
            <span className="ml-3 inline-block text-sm">
              No usage remaining - upgrade your plan to continue using Stark Chat. <u>Billing</u>
            </span>
          </div>
        </Link>
      )}
      <div className="p-5">
        <h1 className="text-h1-small font-semibold font-grotesque">
          Good {greeting}
          {user.name ? `, ${user.name.split(' ')[0]}` : '!'}
        </h1>
      </div>
      <hr className='border-none h-px bg-grey-t-800 shrink-0' />
      <div className="m-5 grid grid-cols-3 gap-x-5">
        <Metric
          loading={loadingUsageSummary}
          name="Users"
          icon={UsersIcon}
          deltaLabel="last 7 days"
          sparkline={usageSummary?.users.sparkline}
          count={usageSummary?.users.current}
          comparator={usageSummary?.users.previous}
        />
        <Metric
          loading={loadingUsageSummary}
          name="Queries"
          icon={ChatBubbleLeftRightIcon}
          deltaLabel="last 7 days"
          sparkline={usageSummary?.queries.sparkline}
          count={usageSummary?.queries.current}
          comparator={usageSummary?.queries.previous}
        />
        <Metric
          loading={loadingUsageSummary}
          name="Pages imported"
          icon={DocumentTextIcon}
          deltaLabel="last 7 days"
          sparkline={usageSummary?.pages.sparkline}
          count={usageSummary?.pages.current}
          comparator={usageSummary?.pages.previous}
        />
      </div>
      <hr className='border-none h-px bg-grey-t-800 shrink-0' />
      <div className="m-5">
        <LineGraph
          data={
            yearlyQueryData?.countQueries.map((c) => ({
              label: format(c.date, 'MMM yy'),
              count: c.count,
            })) ?? []
          }
          loading={loadingYearlyQueryData}
          name="Queries over time"
        />
      </div>
      <hr className='border-none h-px bg-grey-t-800 shrink-0' />
      <div className="m-5">
        <Graph
          data={
            yearlyUniqueUserData?.countQueries.map((c) => ({
              label: format(c.date, 'MMM yy'),
              count: c.count,
            })) ?? []
          }
          loading={loadingYearlyUniqueUserData}
          name="Users over time"
        />
      </div>
      <hr className='border-none h-px bg-grey-t-800 shrink-0' />
    </>
  );
};
